import { Component } from '@theme/component';

/**
 * @typedef {object} SizeGuideModalRefs
 * @property {HTMLDivElement} overlay - The overlay backdrop.
 * @property {HTMLDivElement} panel - The drawer/sheet panel.
 */

/**
 * A size-guide drawer/modal trigger + panel.
 * Desktop: slides in from the right. Mobile: slides up as a bottom sheet.
 * Mirrors the theme's `variant-modal` open/close + overlay/hint pattern.
 *
 * The overlay and panel are portaled to `<body>` on connect so they escape
 * the sticky product-details stacking context and layer above the header.
 *
 * @extends {Component<SizeGuideModalRefs>}
 */
class SizeGuideModal extends Component {
  requiredRefs = ['overlay', 'panel'];

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLElement[]} */
  #portaled = [];

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;

    // Portal overlay + hints + panel to body so fixed positioning is relative
    // to the viewport and not trapped under the sticky product column / header.
    // Once portaled, the theme's `on:click` delegation no longer reaches these
    // nodes, so bind close handlers directly.
    const { overlay, panel } = this.refs;
    const hintDesktop = this.querySelector('.size-guide__hint-desktop');
    const hintMobile = panel?.querySelector('.size-guide__hint-mobile');

    for (const el of [overlay, hintDesktop, panel]) {
      if (el && el.parentElement !== document.body) {
        document.body.appendChild(el);
        this.#portaled.push(el);
      }
    }

    const back = panel?.querySelector('.size-guide__back');

    overlay?.addEventListener('click', this.#close, { signal });
    hintDesktop?.addEventListener('click', this.#close, { signal });
    hintMobile?.addEventListener('click', this.#close, { signal });
    back?.addEventListener('click', this.#close, { signal });

    document.addEventListener('keyup', this.#handleKeyUp, { signal });

    this.#setupContent(signal);
    this.#bindTriggerButtons(signal);
  }

  /**
   * Opens the modal when any `[data-size-guide-trigger]` element in the same
   * section is clicked. Uses event delegation on `.shopify-section` so the
   * trigger link can live anywhere in the block tree (e.g. under Product
   * options) while this content block lives shallower. Mirrors `variant-modal`.
   * @param {AbortSignal} signal
   */
  #bindTriggerButtons(signal) {
    const section = this.closest('.shopify-section');
    if (!section) return;
    section.addEventListener(
      'click',
      (event) => {
        if (/** @type {HTMLElement} */ (event.target).closest('[data-size-guide-trigger]')) {
          event.preventDefault();
          this.open();
        }
      },
      { signal }
    );
  }

  /**
   * Routes child blocks into slots. Two shapes are supported:
   *  - Simple: the source holds `_table` / `_table-note` directly (watches).
   *  - Category: the source holds `_category-table-category` wrappers, each
   *    owning its own table/how-to/feature. A radio list switches between them.
   * An EU/UK/US unit toggle shows/hides cells flagged with data-unit-*.
   * @param {AbortSignal} signal
   */
  #setupContent(signal) {
    const content = this.refs.panel?.querySelector('.size-guide__content');
    if (!content) return;

    this.content = content;
    this.source = content.querySelector('.size-guide__source');
    this.leftSlot = content.querySelector('.size-guide__left');
    this.rightSlot = content.querySelector('.size-guide__right');
    this.featureSlot = content.querySelector('.size-guide__feature');
    this.categoryList = content.querySelector('.size-guide__categories');
    this.unitList = content.querySelector('.size-guide__units');
    this.units = Array.from(content.querySelectorAll('.size-guide__unit'));
    this.videoLink = content.querySelector('.size-guide__video-link');
    this.videoOverlay = content.querySelector('.size-guide__video-overlay');
    this.videoHint = content.querySelector('.size-guide__video-hint');
    this.featureSlot = this.videoOverlay?.querySelector('.size-guide__feature') || this.featureSlot;
    if (!this.source) return;

    // Portal the video overlay to <body> too. The panel uses a transform when
    // open, which would otherwise trap this fixed overlay inside the 420px
    // drawer instead of covering the viewport.
    if (this.videoOverlay && this.videoOverlay.parentElement !== document.body) {
      document.body.appendChild(this.videoOverlay);
      this.#portaled.push(this.videoOverlay);
    }

    // "Show video" opens the video overlay; the hint closes it (tap to close).
    this.videoLink?.addEventListener('click', () => this.#openVideo(), { signal });
    this.videoHint?.addEventListener('click', () => this.#closeVideo(), { signal });
    this.videoOverlay?.addEventListener('click', (e) => {
      if (e.target === this.videoOverlay) this.#closeVideo();
    }, { signal });

    this.categories = Array.from(this.source.querySelectorAll('[data-category-table-category]'));

    if (this.categories.length === 0) {
      // Simple shape: move all source children straight into the left slot.
      Array.from(this.source.children).forEach((child) => {
        const clone = child.cloneNode(true);
        clone.removeAttribute('hidden');
        this.leftSlot.appendChild(clone);
      });
      this.#refreshUnits();
      this.#wireUnits(signal);
      return;
    }

    this.#buildRadios(signal);
    const defaultKey = content.dataset.defaultCategory?.trim();
    const initial = this.categories.find((c) => c.dataset.key === defaultKey) || this.categories[0];
    this.#activateCategory(initial.dataset.key);
    this.#wireUnits(signal);
  }

  /** @param {AbortSignal} signal */
  #buildRadios(signal) {
    if (!this.categoryList) return;
    this.categoryList.hidden = false;
    this.radioButtons = this.categories.map((cat) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'size-guide__cat';
      btn.setAttribute('role', 'radio');
      btn.setAttribute('aria-checked', 'false');
      btn.tabIndex = -1;
      btn.dataset.category = cat.dataset.key;
      btn.textContent = cat.dataset.label || cat.dataset.key;
      btn.addEventListener('click', () => this.#activateCategory(cat.dataset.key), { signal });
      this.categoryList.appendChild(btn);
      return btn;
    });
  }

  /** @param {string} key */
  #activateCategory(key) {
    if (!key) return;
    (this.radioButtons || []).forEach((btn) => {
      const isActive = btn.dataset.category === key;
      btn.setAttribute('aria-checked', String(isActive));
      btn.tabIndex = isActive ? 0 : -1;
    });

    this.leftSlot.replaceChildren();
    this.rightSlot.replaceChildren();
    this.featureSlot.replaceChildren();

    const active = this.categories.find((c) => c.dataset.key === key);
    if (!active) return;

    Array.from(active.children).forEach((child) => {
      const slot = child.dataset && child.dataset.slot;
      const target = slot === 'left' ? this.leftSlot : slot === 'right' ? this.rightSlot : slot === 'feature' ? this.featureSlot : null;
      if (!target) return;
      const clone = child.cloneNode(true);
      clone.removeAttribute('hidden');
      target.appendChild(clone);
    });

    this.#refreshUnits();
    this.#applyUnit(this.content.dataset.activeUnit || 'eu');
    this.#refreshVideoLink();
  }

  /** Show the "Show video" link only when the active category has a video. */
  #refreshVideoLink() {
    if (!this.videoLink) return;
    const hasVideo = !!this.featureSlot?.querySelector('video, iframe, .category-table__feature-media');
    this.videoLink.hidden = !hasVideo;
    this.#closeVideo();
  }

  #openVideo() {
    if (!this.videoOverlay) return;
    this.videoOverlay.hidden = false;
    this.videoOverlay.querySelector('video')?.play?.().catch(() => {});
  }

  #closeVideo() {
    if (!this.videoOverlay) return;
    this.videoOverlay.hidden = true;
    this.videoOverlay.querySelector('video')?.pause?.();
  }

  /** Show the unit toggle only when the active table actually has unit cells. */
  #refreshUnits() {
    this.unitLabels = Array.from(this.content.querySelectorAll('[data-unit-label]'));
    this.unitValues = Array.from(this.content.querySelectorAll('[data-unit-value]'));
    if (this.unitList) this.unitList.hidden = this.unitLabels.length === 0;
  }

  /** @param {AbortSignal} signal */
  #wireUnits(signal) {
    this.units.forEach((btn) => {
      btn.addEventListener('click', () => this.#activateUnit(btn.dataset.unit), { signal });
    });
    this.#applyUnit(this.content.dataset.activeUnit || 'eu');
  }

  /** @param {string} unit */
  #activateUnit(unit) {
    if (!unit) return;
    this.content.dataset.activeUnit = unit;
    this.units.forEach((btn) => {
      const isActive = btn.dataset.unit === unit;
      btn.setAttribute('aria-selected', String(isActive));
      btn.tabIndex = isActive ? 0 : -1;
    });
    this.#applyUnit(unit);
  }

  /** @param {string} unit */
  #applyUnit(unit) {
    (this.unitLabels || []).forEach((node) => {
      node.toggleAttribute('hidden', node.dataset.unitLabel !== unit);
    });
    (this.unitValues || []).forEach((node) => {
      node.toggleAttribute('hidden', node.dataset.unitValue !== unit);
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.close();
    for (const el of this.#portaled) el.remove();
    this.#portaled = [];
    this.#abortController.abort();
  }

  /** @returns {boolean} */
  get isOpen() {
    return this.hasAttribute('open');
  }

  open() {
    if (this.isOpen) return;
    this.setAttribute('open', '');
    for (const el of this.#portaled) el.classList.add('is-open');
    document.documentElement.setAttribute('scroll-lock', '');
  }

  close() {
    if (!this.isOpen) return;
    this.removeAttribute('open');
    for (const el of this.#portaled) el.classList.remove('is-open');
    this.#closeVideo();
    document.documentElement.removeAttribute('scroll-lock');
  }

  /** Bound close handler for directly-attached listeners on portaled nodes. */
  #close = () => this.close();

  /** @param {KeyboardEvent} event */
  #handleKeyUp = (event) => {
    if (event.key === 'Escape' && this.isOpen) this.close();
  };
}

if (!customElements.get('size-guide-modal')) {
  customElements.define('size-guide-modal', SizeGuideModal);
}
